import React, { useRef, useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import boundaryData from './sangli-boundary.json';
import 'leaflet/dist/leaflet.css';
import * as turf from '@turf/turf';

const FlyToVillage = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.flyToBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);
  return null;
};

const FarmMap = ({ selectedVillage, selectedTaluka, showSelector = false }) => {
  const mapRef = useRef();
  const [selectedBounds, setSelectedBounds] = useState(null);
  const [currentVillage, setCurrentVillage] = useState(selectedVillage);
  const [cropResults, setCropResults] = useState({});

  const villageOptions = boundaryData.features.map(
    (feature) => feature.properties.VILLNAME || feature.properties.name || 'Unknown'
  );

  const getVillagesForTaluka = (taluka) => {
    if (!taluka) return villageOptions;
    const sangolaVillages = ['सांगोला', 'कुरुंदवाड', 'मोहोळ', 'तळेगाव', 'दापोली', 'धामणी', 'नंदणी', 'वाळव्या'];
    const atpadiVillages = ['आटपाडी', 'यावल', 'दसूर', 'फलटण', 'निगडी', 'महाळुंग', 'नेरली', 'मोरवाडी'];
    if (taluka === 'सांगोला') return sangolaVillages;
    if (taluka === 'आटपाडी') return atpadiVillages;
    return villageOptions;
  };

  const filteredVillages = getVillagesForTaluka(selectedTaluka);

  const handleVillageSelect = (e) => {
    const selected = e.target.value;
    setCurrentVillage(selected);
    const feature = boundaryData.features.find(
      (f) => f.properties.VILLNAME === selected || f.properties.name === selected
    );
    if (feature) {
      const geojsonLayer = L.geoJSON(feature);
      setSelectedBounds(geojsonLayer.getBounds());
    }
  };

  useEffect(() => {
    if (selectedVillage) {
      setCurrentVillage(selectedVillage);
      const feature = boundaryData.features.find(
        (f) => f.properties.VILLNAME === selectedVillage || f.properties.name === selectedVillage
      );
      if (feature) {
        const geojsonLayer = L.geoJSON(feature);
        setSelectedBounds(geojsonLayer.getBounds());
      }
    }
  }, [selectedVillage]);

  const detectCrop = async (geometry) => {
    try {
      const res = await fetch('http://168.231.121.98:5000/detect-crop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geojson: { type: 'Feature', geometry } })
      });
      const result = await res.json();
      return result;
    } catch (error) {
      console.error('Crop detection failed:', error);
      return null;
    }
  };

  const onEachVillage = (feature, layer) => {
    const name = feature.properties.VILLNAME || feature.properties.name || 'Unknown';
    const taluka = feature.properties.TALUKA || selectedTaluka || 'N/A';
    const isSelected = name === currentVillage;

    layer.setStyle({
      color: isSelected ? '#dc2626' : 'lime',
      weight: isSelected ? 3 : 2,
      fillOpacity: isSelected ? 0.3 : 0.1,
      fillColor: isSelected ? '#fca5a5' : '#84cc16',
    });

    layer.on('click', async () => {
      const polygon = turf.polygon(feature.geometry.coordinates);
      const areaHectares = (turf.area(polygon) / 10000).toFixed(2);

      let cropContent = `<p><b>🌾 पिकांची माहिती उपलब्ध नाही</b></p>`;
      if (cropResults[name]) {
        const { mean_ndvi, likely_crop, confidence } = cropResults[name];
        cropContent = `
          <p><b>🌱 NDVI:</b> ${mean_ndvi}</p>
          <p><b>🌿 अंदाजित पीक:</b> ${likely_crop}</p>
          <p><b>📊 आत्मविश्वास:</b> ${confidence}</p>
        `;
      } else {
        const result = await detectCrop(feature.geometry);
        if (result?.likely_crop) {
          setCropResults((prev) => ({ ...prev, [name]: result }));
          cropContent = `
            <p><b>🌱 NDVI:</b> ${result.mean_ndvi}</p>
            <p><b>🌿 अंदाजित पीक:</b> ${result.likely_crop}</p>
            <p><b>📊 आत्मविश्वास:</b> ${result.confidence}</p>
          `;
        }
      }

      layer.bindPopup(`
        <div style="font-family: Arial, sans-serif;">
          <h4><b>${name}</b></h4>
          <p><b>📏 क्षेत्रफळ:</b> ${areaHectares} हेक्टर</p>
          <p><b>📍 तालुका:</b> ${taluka}</p>
          <p><b>🏛️ जिल्हा:</b> सांगली</p>
          ${cropContent}
        </div>
      `).openPopup();

      setCurrentVillage(name);
    });

    layer.on('mouseover', () => {
      if (!isSelected) {
        layer.setStyle({
          color: '#22c55e',
          weight: 3,
          fillOpacity: 0.2,
        });
      }
    });

    layer.on('mouseout', () => {
      if (!isSelected) {
        layer.setStyle({
          color: 'lime',
          weight: 2,
          fillOpacity: 0.1,
        });
      }
    });
  };

  const getMapCenter = () => {
    if (selectedTaluka === 'सांगोला') return [17.174, 74.568];
    if (selectedTaluka === 'आटपाडी') return [17.190, 74.610];
    return [17.174, 74.568];
  };

  const getMapZoom = () => (selectedVillage ? 15 : 12);

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      {showSelector && (
        <div style={{ padding: '10px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <label style={{ fontWeight: '600', color: '#374151' }}>गाव निवडा: </label>
          <select
            onChange={handleVillageSelect}
            value={currentVillage || ''}
            style={{
              marginLeft: '8px',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
            }}
          >
            <option value="" disabled>गाव निवडा</option>
            {filteredVillages.map((name, idx) => (
              <option key={idx} value={name}>{name}</option>
            ))}
          </select>
        </div>
      )}

      <MapContainer
        center={getMapCenter()}
        zoom={getMapZoom()}
        style={{ height: showSelector ? 'calc(100vh - 50px)' : '100vh', width: '100%' }}
        whenCreated={(mapInstance) => { mapRef.current = mapInstance; }}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles © Esri"
        />
        {boundaryData?.features?.length > 0 && (
          <GeoJSON data={boundaryData} onEachFeature={onEachVillage} />
        )}
        {selectedBounds && <FlyToVillage bounds={selectedBounds} />}
      </MapContainer>
    </div>
  );
};

export default FarmMap;
